import { CallApi, handleJSONResponse } from './common';
import { ListCatalogResponse } from '@services/merch/types/api/list-catalog';
import { GetCatalogItemResponse } from '@services/merch/types/api/get-catalog-item';
import {
  UploadImageRequest,
  UploadImageResponse,
} from '@services/merch/types/api/upload-image';
import {
  CreateProductRequest,
  CreateProductResponse,
  ShippingCostApiResponse,
} from '@services/merch/types/api/create-product';
import { ListProductResponse } from '@services/merch/types/api/list-product';
import { TakeDownProductResponse } from '@services/merch/types/api/take-down-product';
import { PublishProductResponse } from '@services/merch/types/api/publish-product';
import { UpdateProductRequest } from '@services/merch/types/api/update-product';
import { MerchProduct } from '@services/merch/types/api/merch-product';
import {
  CreateMerchOrderRequest,
  CreateMerchOrderResponse,
} from '@services/merch/types/api/create-merch-order';
import { MerchProductStatus } from '@services/merch/types/api/merch-product-status';

const callAPI = CallApi('merch');

export const listCatalog = async (): Promise<ListCatalogResponse> => {
  const response = await callAPI(`/catalog`, 'GET');

  return handleJSONResponse(response);
};

export const listCatalogItem = async (
  catalogId: number
): Promise<GetCatalogItemResponse> => {
  const response = await callAPI(`/catalog/${catalogId}`, 'GET');

  return handleJSONResponse(response);
};

export const uploadImage = async (
  data: UploadImageRequest,
  artistId: string
): Promise<UploadImageResponse> => {
  const response = await callAPI(`/${artistId}/product/image`, 'POST', data);

  return await handleJSONResponse(response);
};

export const getImage = async (
  artistId: string,
  imageId: string
): Promise<UploadImageResponse> => {
  const response = await callAPI(
    `/${artistId}/product/image/${imageId}`,
    'GET'
  );

  return await handleJSONResponse(response);
};

export const createMerch = async (
  data: CreateProductRequest,
  artistId: string
): Promise<CreateProductResponse> => {
  const response = await callAPI(`/${artistId}/product`, 'POST', data);

  return await handleJSONResponse(response);
};

export const editMerch = async (
  data: UpdateProductRequest,
  productId: string,
  artistId: string
): Promise<MerchProduct> => {
  const response = await callAPI(
    `/${artistId}/product/${productId}`,
    'PUT',
    data
  );

  return await handleJSONResponse(response);
};

export const editMerchStatus = async (
  status: MerchProductStatus,
  productId: string,
  artistId: string
): Promise<MerchProduct> => {
  const response = await callAPI(
    `/${artistId}/product/${productId}/status`,
    'PUT',
    { status }
  );

  return await handleJSONResponse(response);
};

export const listMerchProducts = async (
  artistId: string
): Promise<ListProductResponse> => {
  const response = await callAPI(`/${artistId}/product`, 'GET');

  return handleJSONResponse(response);
};

export const getProduct = async (
  artistId: string,
  productId: string
): Promise<MerchProduct> => {
  const response = await callAPI(`/${artistId}/product/${productId}`, 'GET');

  return handleJSONResponse(response);
};

export const takeDown = async (
  artistId: string,
  productId: string
): Promise<TakeDownProductResponse> => {
  const response = await callAPI(
    `/${artistId}/product/${productId}/takedown`,
    'DELETE'
  );

  return handleJSONResponse(response);
};

export const publish = async (
  artistId: string,
  productId: string
): Promise<PublishProductResponse> => {
  const response = await callAPI(
    `/${artistId}/product/${productId}/publish`,
    'POST'
  );

  return handleJSONResponse(response);
};

export const updateOrder = async (
  artistId: string,
  products: string[]
): Promise<string[]> => {
  const response = await callAPI(`/${artistId}/product/order`, 'PUT', {
    products,
  });

  return handleJSONResponse(response);
};

export const createMerchSampleOrder = async (
  data: CreateMerchOrderRequest
): Promise<CreateMerchOrderResponse> => {
  const response = await callAPI(`/merch-order`, 'POST', data);

  return await handleJSONResponse(response);
};

export const getMerchShippingCost = async (
  data: CreateMerchOrderRequest
): Promise<ShippingCostApiResponse> => {
  const response = await callAPI(`/order/shipping-cost`, 'POST', data);

  return await handleJSONResponse(response);
};

export const confirmMerchOrder = async (
  orderId: string,
  total: number | null,
  subtotal: number | null,
  shipping_cost: number | null | undefined,
  total_tax: number | null | undefined,
  currency: string | null,
  stripe_checkout_id: string
) => {
  const response = await callAPI('/merch/confirm-merch-order', 'POST', {
    orderId,
    total,
    subtotal,
    shipping_cost,
    total_tax,
    currency,
    stripe_checkout_id,
  });

  return await handleJSONResponse(response);
};
